import { EnvConfig } from '../types/EnvConfig';

/**
 * Flags del sitio publico leidos en runtime del BFF (Bug 22942).
 *
 * Por que: prender un modulo dependia de cambiar variables de entorno del contenedor (DevOps).
 * Ahora el BFF es la fuente: `GET {bff_url}/public/feature-flags` devuelve
 * `{ autoViaBff, landings, retomar, ventaPresencial }`.
 *
 * Reglas:
 * - Fail-closed: si la llamada falla, tarda mas de TIMEOUT_FLAGS_MS o el cuerpo no sirve, todo
 *   queda en false. La web publica no puede romperse por un flag.
 * - Con respuesta del BFF, manda el BFF (un false del BFF gana siempre).
 * - Sin respuesta, `react_config.json` es solo el default de localhost (desarrollo).
 * - Los mocks (`landings_mock`, `retomar_mock`) solo valen en localhost.
 */

export interface FlagsBff {
  autoViaBff: boolean;
  landings: boolean;
  retomar: boolean;
  ventaPresencial: boolean;
}

export const TIMEOUT_FLAGS_MS = 2000;

const RUTA_FLAGS = '/public/feature-flags';

/** Clave del flag en el BFF -> clave en EnvConfig (la que leen las pantallas). */
const CLAVES: Record<keyof FlagsBff, keyof EnvConfig> = {
  autoViaBff: 'auto_via_bff',
  landings: 'landings_enabled',
  retomar: 'retomar_enabled',
  ventaPresencial: 'venta_presencial_enabled',
};

/** Claves de EnvConfig que son flags efectivos: se resuelven en cada carga de página y NUNCA se persisten. */
export const CLAVES_FLAGS_CONFIG: ReadonlyArray<keyof EnvConfig> = [
  ...Object.values(CLAVES),
  'landings_mock',
  'retomar_mock',
];

/** Copia del config sin ninguna clave de flag (lo que sí se puede guardar en origen-storage). */
export const sinFlags = (config: EnvConfig): EnvConfig => {
  const copia = { ...config } as unknown as Record<string, unknown>;
  CLAVES_FLAGS_CONFIG.forEach((clave) => {
    delete copia[clave];
  });
  return copia as unknown as EnvConfig;
};

type FetchImpl = (url: string, init?: RequestInit) => Promise<Response>;

export const esHostLocal = (hostname: string): boolean =>
  hostname === 'localhost' || hostname === '127.0.0.1';

const esObjeto = (valor: unknown): valor is Record<string, unknown> =>
  typeof valor === 'object' && valor !== null && !Array.isArray(valor);

/** Solo `true` booleano prende un flag: "true", 1 o ausente quedan en false. */
const aFlags = (cuerpo: Record<string, unknown>): FlagsBff => ({
  autoViaBff: cuerpo.autoViaBff === true,
  landings: cuerpo.landings === true,
  retomar: cuerpo.retomar === true,
  ventaPresencial: cuerpo.ventaPresencial === true,
});

/** Flags del BFF, o null si no se pudieron obtener a tiempo. Nunca tira. */
export const obtenerFlagsBff = async (
  bffUrl: string | undefined,
  fetchImpl: FetchImpl | undefined = typeof fetch === 'function' ? fetch : undefined,
  timeoutMs: number = TIMEOUT_FLAGS_MS
): Promise<FlagsBff | null> => {
  if (!bffUrl || !fetchImpl) return null;
  const url = bffUrl.replace(/\/+$/, '') + RUTA_FLAGS;
  const controller = typeof AbortController === 'function' ? new AbortController() : undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const vencido = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      controller?.abort();
      resolve(null);
    }, timeoutMs);
  });
  const pedido = (async (): Promise<FlagsBff | null> => {
    try {
      const respuesta = await fetchImpl(url, { cache: 'no-store', signal: controller?.signal });
      if (!respuesta.ok) return null;
      const cuerpo: unknown = await respuesta.json();
      return esObjeto(cuerpo) ? aFlags(cuerpo) : null;
    } catch {
      // Red caida, CORS, abort por timeout o JSON invalido: mismo resultado, fail-closed.
      return null;
    }
  })();
  try {
    return await Promise.race([pedido, vencido]);
  } finally {
    clearTimeout(timer);
  }
};

/** Config con los flags efectivos. Pura: no hace I/O. */
export const aplicarFlags = (config: EnvConfig, flags: FlagsBff | null, local: boolean): EnvConfig => {
  const efectivo: EnvConfig = { ...config };
  (Object.keys(CLAVES) as Array<keyof FlagsBff>).forEach((clave) => {
    const destino = CLAVES[clave];
    const valor = flags ? flags[clave] : local && config[destino] === true;
    (efectivo as unknown as Record<string, unknown>)[destino] = valor;
  });
  efectivo.landings_mock = local && config.landings_mock === true;
  efectivo.retomar_mock = local && config.retomar_mock === true;
  return efectivo;
};

export interface OpcionesFlags {
  fetchImpl?: FetchImpl;
  hostname?: string;
  timeoutMs?: number;
}

/** Baja los flags del BFF y los aplica sobre el config de `react_config.json`. Nunca tira. */
export const resolverFlags = async (config: EnvConfig, opciones: OpcionesFlags = {}): Promise<EnvConfig> => {
  const hostname = opciones.hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  // Misma base que getBffBaseUrl: react_config manda, .env como fallback de dev.
  const bffUrl = config.bff_url || process.env.REACT_APP_API_BASE;
  const flags = await obtenerFlagsBff(bffUrl, opciones.fetchImpl, opciones.timeoutMs);
  return aplicarFlags(config, flags, esHostLocal(hostname));
};
